/* ──────────────────────────────────────────────────────────────
 *  useSampleSelection — selects a sample and, if its geometry
 *  for the current compute job isn't already generated, drives
 *  the same loading overlay used by Compute while the /mesh
 *  call(s) run.
 *
 *  Entry points that should use selectSampleWithGeometry instead
 *  of calling useSampleStore.selectSample directly:
 *    - SearchBar (Enter-key search)
 *    - ScatterChart's onSelect (graph click) — wired by its parent
 *    - ExploreDialog's Confirm button
 * ────────────────────────────────────────────────────────────── */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useSampleStore } from '@/stores/useSampleStore';
import { useJobStore } from '@/stores/useJobStore';
import { useUIStore } from '@/stores/useUIStore';
import { useBushingStore } from '@/stores/useBushingStore';
import {
  generateGeometryWithProgress,
  isGeometryFullyCached,
  type GeometryGenParams,
} from '@/utils/geometryGeneration';

export interface UseSampleSelectionReturn {
  selectSampleWithGeometry: (id: string | null) => Promise<void>;
}

export function useSampleSelection(): UseSampleSelectionReturn {
  const queryClient = useQueryClient();

  const selectSampleWithGeometry = useCallback(
    async (id: string | null) => {
      const selectSample = useSampleStore.getState().selectSample;

      // Clearing selection — nothing to fetch.
      if (!id) {
        selectSample(null);
        return;
      }

      const { genRowsById, geometryConfigSnapshot } = useSampleStore.getState();
      const sample = genRowsById[id];
      const computeJobId = useJobStore.getState().computeJobId;

      // Not a generated row (e.g. a training-data point), or no compute
      // job / frozen config yet to generate geometry against — just select.
      if (!sample || !computeJobId || !geometryConfigSnapshot) {
        selectSample(id);
        return;
      }

      const bushingType = useBushingStore.getState().bushingType;
      const rawSampleData = sample as unknown as Record<string, number>;

      // Mirror the frozen config used at compute time — NOT live config —
      // so switching samples renders geometry consistent with what
      // Compute actually generated.
      const effectiveMinArea =
        geometryConfigSnapshot.weightPockets && geometryConfigSnapshot.coreMaterial !== 'Steel'
          ? geometryConfigSnapshot.minContactArea ??
            geometryConfigSnapshot.calculatedMinContactArea ??
            0
          : 0;

      const geometryParams: Omit<GeometryGenParams, 'progressStart' | 'progressEnd'> = {
        queryClient,
        jobId: computeJobId,
        sampleId: id,
        bushingType,
        rawSampleData,
        coreMaterial: geometryConfigSnapshot.coreMaterial,
        outerMaterial: geometryConfigSnapshot.outerMaterial,
        interSleeveMaterial: geometryConfigSnapshot.interSleeveMaterial,
        coreId: geometryConfigSnapshot.coreId,
        effectiveMinArea,
        weightPockets: geometryConfigSnapshot.weightPockets,
        sleeveThickness: geometryConfigSnapshot.sleeveThickness,
        linearTravelRadialPlus: geometryConfigSnapshot.linearTravelRadialPlus,
        linearTravelRadialMinus: geometryConfigSnapshot.linearTravelRadialMinus,
      };

      // Already generated for this job/sample — switch instantly, no overlay.
      if (isGeometryFullyCached(geometryParams)) {
        selectSample(id);
        return;
      }

      selectSample(id);
      useUIStore.getState().showLoading('Generating geometry…');
      useUIStore.getState().updateProgress(5, 'Generating geometry…');

      try {
        await generateGeometryWithProgress({
          ...geometryParams,
          progressStart: 5,
          progressEnd: 100,
        });
      } finally {
        useUIStore.getState().hideLoading();
      }
    },
    [queryClient],
  );

  return { selectSampleWithGeometry };
}